import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import { SortType } from "../../../models/SortType";
import {
  formatDate,
  formatDateWithoutTime,
} from "../common/functions/formatDate";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import { PurchaseOrderInvoice } from "../../../models/Invoice/PurchaseOrderInvoice";
import { useNavigate } from "react-router-dom";

interface PurchaseOrderInvoicesTableProp {
  maxHeight: number;
  purchaseOrderInvoices: PurchaseOrderInvoice[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy?: string;
  sortType?: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
}

const PurchaseOrderInvoicesTable = ({
  maxHeight,
  purchaseOrderInvoices,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: PurchaseOrderInvoicesTableProp) => {
  const theme = useTheme<Theme>();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const handleDownloadLink = (url: string, filename?: string) => {
    if (!url || url.trim() === "") return;
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    if (filename) {
      link.download = filename;
    }
    link.click();
  };

  const navigate = useNavigate();

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      {purchaseOrderInvoices.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead sx={{ position: "relative", zIndex: 30 }}>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      position: "sticky",
                      left: 0,
                      top: 0,
                      zIndex: 30,
                      minWidth: "60px",
                      backgroundColor: theme.palette.background.paper,
                      isolation: "isolate",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "100px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "po_id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "po_id",
                          sortBy === "po_id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("po_id")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      PO ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "150px",
                    }}
                  >
                    Vendor Name
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "150px",
                    }}
                  >
                    Invoice Number
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "120px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "invoice_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "invoice_date",
                          sortBy === "invoice_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("invoice_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Invoice Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "120px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "invoice_amount"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "invoice_amount",
                          sortBy === "invoice_amount" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("invoice_amount")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Invoice Amount
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "created_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "created_date",
                          sortBy === "created_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("created_date")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Uploaded Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    Payment Term
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "120px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "expected_payment_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "expected_payment_date",
                          sortBy === "expected_payment_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("expected_payment_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Expected Payment Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    Approval
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    Payment Status
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "120px",
                    }}
                  >
                    Approved By Admin ID
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "140px",
                    }}
                  >
                    Approved Invoice Date
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "120px",
                    }}
                  >
                    Rejected By Admin ID
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "140px",
                    }}
                  >
                    Rejection Reason
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "140px",
                    }}
                  >
                    Rejected Invoice Date
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      minWidth: "100px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "active"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "active",
                          sortBy === "active" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("active")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Active
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    Modified Date
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrderInvoices.map((purchaseOrderInvoice) => {
                  return (
                    <TableRow
                      key={purchaseOrderInvoice.id}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          left: 0,
                          position: "sticky",
                          zIndex: 10,
                          boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3)",
                          backgroundColor: theme.palette.background.paper,
                          fontWeight: 700,
                          minWidth: "80px",
                          textDecoration: "underline",
                          cursor: "pointer",
                          "&:hover": {
                            color: theme.palette.customColors.purpleMain,
                          },
                        }}
                        onClick={() => {
                          navigate(
                            `/purchase/requests/details?prId=${purchaseOrderInvoice.pr_id}&projectId=${purchaseOrderInvoice.order_id}`,
                          );
                        }}
                      >
                        {purchaseOrderInvoice.id}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {purchaseOrderInvoice.po_id || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {purchaseOrderInvoice.vendor_name || "-"} (
                        {purchaseOrderInvoice.vendor_id || "-"})
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <span>
                            {purchaseOrderInvoice.invoice_number || "-"}
                          </span>
                          {purchaseOrderInvoice.invoice_url ? (
                            <>
                              <Tooltip title="Download Invoice">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDownloadLink(
                                      purchaseOrderInvoice.invoice_url!,
                                      `Invoice_${
                                        purchaseOrderInvoice.invoice_number ||
                                        purchaseOrderInvoice.id
                                      }.pdf`,
                                    )
                                  }
                                  sx={{
                                    color: theme.palette.primary.main,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.action.hover,
                                    },
                                  }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : null}
                        </Box>
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(purchaseOrderInvoice.invoice_date)}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {purchaseOrderInvoice.invoice_amount
                          ? `₹${purchaseOrderInvoice.invoice_amount.toLocaleString(
                              "en-IN",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}`
                          : "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(purchaseOrderInvoice.created_date)}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {purchaseOrderInvoice.payment_term || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDateWithoutTime(
                          purchaseOrderInvoice.expected_payment_date,
                        ) || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell sx={{ textAlign: "center" }}>
                        <Chip
                          label={
                            purchaseOrderInvoice.approve_invoice === 1
                              ? "Approved"
                              : purchaseOrderInvoice.reject_invoice === 1
                                ? "Rejected"
                                : "Pending"
                          }
                          size="small"
                          color={
                            purchaseOrderInvoice.approve_invoice === 1
                              ? "success"
                              : purchaseOrderInvoice.reject_invoice === 1
                                ? "error"
                                : "warning"
                          }
                          variant="filled"
                          sx={{ fontSize: "11px", height: 22 }}
                        />
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        <Chip
                          label={
                            purchaseOrderInvoice.invoice_payment_status ===
                            "paid"
                              ? "Paid"
                              : purchaseOrderInvoice.invoice_payment_status ===
                                  "partial_paid"
                                ? "Partial"
                                : "Pending"
                          }
                          size="small"
                          color={
                            purchaseOrderInvoice.invoice_payment_status ===
                            "paid"
                              ? "success"
                              : purchaseOrderInvoice.invoice_payment_status ===
                                  "partial_paid"
                                ? "info"
                                : "warning"
                          }
                          variant="filled"
                          sx={{ fontSize: "11px", height: 22 }}
                        />
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {purchaseOrderInvoice.approved_by_admin_id || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(purchaseOrderInvoice.approved_invoice_date)}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {purchaseOrderInvoice.rejected_by_admin_id || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {purchaseOrderInvoice.rejection_reason || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(purchaseOrderInvoice.rejected_invoice_date)}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {purchaseOrderInvoice.active !== undefined ? (
                          <Chip
                            label={
                              purchaseOrderInvoice.active
                                ? "Active"
                                : "Inactive"
                            }
                            size="small"
                            color={
                              purchaseOrderInvoice.active ? "success" : "error"
                            }
                            variant="outlined"
                          />
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(purchaseOrderInvoice.modified_date)}
                      </AppTableRowCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default PurchaseOrderInvoicesTable;
